/**
 * IndoorV3QueryAdapter.ts
 *
 * Explicit Mapbox Indoor v3 Vector Source & Read-Only Query Adapter
 *
 * Responsibilities:
 * 1. Safely registers `mapbox://mapbox.indoor-v3` as a READ-ONLY vector source (`wingman-mapbox-indoor-v3`).
 * 2. Enforces a strict allowlist of the 8 documented Mapbox Indoor v3 source layers.
 * 3. Provides bounded query execution distinguishing SOURCE_NOT_READY, LAYER_NOT_AVAILABLE,
 *    NO_FEATURES_IN_LOADED_TILE, and FEATURES_FOUND.
 * 4. Strictly DATA ONLY — zero visual layers added, preserving Mapbox Standard basemap visual authority.
 *
 * Preserves all platform contracts:
 *   Contract 3 (FlyTo): Zero camera mutation
 *   Contract 4 (Spatial): Standard Mapbox addSource / querySourceFeatures APIs
 */

import type mapboxgl from 'mapbox-gl';
import { telemetry } from '../utils/telemetry';

export const MAPBOX_INDOOR_V3_SOURCE_ID = 'wingman-mapbox-indoor-v3';
export const MAPBOX_INDOOR_V3_SOURCE_URL = 'mapbox://mapbox.indoor-v3';
export const MAPBOX_INDOOR_V3_SUBSCRIPTION_LAYER_ID = 'wingman-indoor-v3-data-subscription';

export const APPROVED_INDOOR_V3_LAYERS = [
    'indoor_structure',
    'indoor_floor',
    'indoor_floorplan',
    'indoor_door',
    'indoor_label',
    'indoor_facility_metadata',
    'indoor_structure_metadata',
    'indoor_floor_metadata',
] as const;

export type IndoorV3LayerName = typeof APPROVED_INDOOR_V3_LAYERS[number];

export type IndoorV3AccessStatus =
    | 'UNINITIALIZED'
    | 'SOURCE_REGISTERED'
    | 'SOURCE_READY'
    | 'LAYER_NOT_AVAILABLE'
    | 'NO_FEATURES_IN_LOADED_TILE'
    | 'FEATURES_FOUND';

export interface IndoorV3QueryResult<T = any> {
    layer: string;
    isAllowlisted: boolean;
    status: IndoorV3AccessStatus;
    features: T[];
    error?: string;
}

export class IndoorV3QueryAdapter {
    private registeredMaps = new WeakSet<object>();

    /**
     * Ensure the explicit Mapbox Indoor v3 vector source and its data-subscription
     * layer are registered on the map.
     * Strictly idempotent — will not duplicate source or layer if already present.
     * The subscription layer is strictly non-visual (fill-opacity: 0) to ensure
     * Mapbox GL JS activates vector tile fetching without altering basemap appearance.
     */
    ensureSource(map: mapboxgl.Map): boolean {
        if (!map || typeof (map as any).getSource !== 'function' || typeof (map as any).addSource !== 'function') {
            return false;
        }

        try {
            // Check if source already registered on the Mapbox style
            const existing = map.getSource(MAPBOX_INDOOR_V3_SOURCE_ID);
            if (!existing) {
                // Register vector source
                map.addSource(MAPBOX_INDOOR_V3_SOURCE_ID, {
                    type: 'vector',
                    url: MAPBOX_INDOOR_V3_SOURCE_URL,
                });

                telemetry.info('INDOOR_V3', 'SOURCE_REGISTERED', {
                    sourceId: MAPBOX_INDOOR_V3_SOURCE_ID,
                });
            }

            // Register non-visual data-subscription layer to trigger Mapbox GL JS tile loading.
            // Mapbox GL JS marks a vector source as unused and skips downloading .pbf tiles
            // unless at least one style layer references the source.
            if (typeof (map as any).getLayer === 'function' && typeof (map as any).addLayer === 'function') {
                if (!map.getLayer(MAPBOX_INDOOR_V3_SUBSCRIPTION_LAYER_ID)) {
                    map.addLayer({
                        id: MAPBOX_INDOOR_V3_SUBSCRIPTION_LAYER_ID,
                        type: 'fill',
                        source: MAPBOX_INDOOR_V3_SOURCE_ID,
                        'source-layer': 'indoor_floorplan',
                        paint: {
                            'fill-opacity': 0,
                        },
                    });
                }
            }

            this.registeredMaps.add(map);
            return true;
        } catch (e) {
            telemetry.warn('INDOOR_V3', 'SOURCE_REGISTRATION_FAILED', {
                error: String(e),
            });
            return false;
        }
    }

    /**
     * Check if the explicit source is registered and ready on the map instance.
     */
    isSourceReady(map: mapboxgl.Map): boolean {
        if (!map) return false;
        try {
            const source = map.getSource(MAPBOX_INDOOR_V3_SOURCE_ID);
            if (!source) return false;
            if (typeof (map as any).isSourceLoaded === 'function') {
                return (map as any).isSourceLoaded(MAPBOX_INDOOR_V3_SOURCE_ID);
            }
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Query a specific Indoor v3 source layer.
     * Rejects unapproved layers and differentiates empty loaded tile results from global absence.
     */
    queryLayer<T = any>(
        map: mapboxgl.Map,
        sourceLayer: IndoorV3LayerName,
        filter?: any[]
    ): IndoorV3QueryResult<T> {
        // 1. Strict allowlist validation
        if (!APPROVED_INDOOR_V3_LAYERS.includes(sourceLayer as any)) {
            telemetry.warn('INDOOR_V3', 'UNAPPROVED_LAYER_REJECTED', { layer: sourceLayer });
            return {
                layer: sourceLayer,
                isAllowlisted: false,
                status: 'LAYER_NOT_AVAILABLE',
                features: [],
                error: `Layer "${sourceLayer}" is not in approved allowlist [${APPROVED_INDOOR_V3_LAYERS.join(', ')}]`,
            };
        }

        // 2. Validate map & source readiness
        if (!map) {
            return {
                layer: sourceLayer,
                isAllowlisted: true,
                status: 'SOURCE_NOT_READY',
                features: [],
                error: 'Map instance not provided',
            };
        }

        try {
            const source = map.getSource(MAPBOX_INDOOR_V3_SOURCE_ID);
            if (!source) {
                return {
                    layer: sourceLayer,
                    isAllowlisted: true,
                    status: 'SOURCE_NOT_READY',
                    features: [],
                    error: `Source "${MAPBOX_INDOOR_V3_SOURCE_ID}" not registered on map style`,
                };
            }

            // 3. Query source features safely
            const queryParams: { sourceLayer: string; filter?: any[] } = {
                sourceLayer,
            };
            if (filter) {
                queryParams.filter = filter;
            }

            const rawFeatures = map.querySourceFeatures(MAPBOX_INDOOR_V3_SOURCE_ID, queryParams) || [];

            if (rawFeatures.length > 0) {
                return {
                    layer: sourceLayer,
                    isAllowlisted: true,
                    status: 'FEATURES_FOUND',
                    features: rawFeatures as T[],
                };
            }

            return {
                layer: sourceLayer,
                isAllowlisted: true,
                status: 'NO_FEATURES_IN_LOADED_TILE',
                features: [],
            };
        } catch (e) {
            telemetry.warn('INDOOR_V3', 'QUERY_FAILED', {
                layer: sourceLayer,
                error: String(e),
            });
            return {
                layer: sourceLayer,
                isAllowlisted: true,
                status: 'SOURCE_NOT_READY',
                features: [],
                error: String(e),
            };
        }
    }

    /**
     * Clean up registration state for a map instance.
     * Safely removes dependent subscription layer before removing the source.
     */
    cleanup(map: mapboxgl.Map): void {
        if (!map) return;
        try {
            if (typeof (map as any).getLayer === 'function' && typeof (map as any).removeLayer === 'function') {
                if (map.getLayer(MAPBOX_INDOOR_V3_SUBSCRIPTION_LAYER_ID)) {
                    map.removeLayer(MAPBOX_INDOOR_V3_SUBSCRIPTION_LAYER_ID);
                }
            }
            if (typeof (map as any).getSource === 'function' && typeof (map as any).removeSource === 'function') {
                if (map.getSource(MAPBOX_INDOOR_V3_SOURCE_ID)) {
                    map.removeSource(MAPBOX_INDOOR_V3_SOURCE_ID);
                }
            }
        } catch {
            // Ignore removal errors during teardown
        }
    }
}

// Singleton export
export const indoorV3QueryAdapter = new IndoorV3QueryAdapter();

// Global window debug access
if (typeof window !== 'undefined') {
    (window as any).WingmanIndoorV3 = {
        ensureSource: (map: mapboxgl.Map) => indoorV3QueryAdapter.ensureSource(map),
        isReady: (map: mapboxgl.Map) => indoorV3QueryAdapter.isSourceReady(map),
        queryLayer: (map: mapboxgl.Map, layer: IndoorV3LayerName, filter?: any[]) =>
            indoorV3QueryAdapter.queryLayer(map, layer, filter),
        allowlist: APPROVED_INDOOR_V3_LAYERS,
    };
}
